import React, { useEffect, useState } from 'react';
import { FormControl, FormHelperText, Icon, TextField } from '@mui/material';

import DialogBase from '../dialogs/DialogBase';
import { LayerViewModel } from '../../../models/maps/layerViewModel';

interface DialogAssetLayerProps {
  open: boolean;
  layer?: LayerViewModel | null;
  onClose: () => void;
  onSubmit: (layer: LayerViewModel) => void;
}

const MAX_DESCRIPTION_LENGTH = 2000;

const DialogAssetLayer: React.FC<DialogAssetLayerProps> = ({
  open,
  layer,
  onClose,
  onSubmit,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [validationErrors, setValidationErrors] = useState<{
    displayName?: string;
    description?: string;
  }>({});

  useEffect(() => {
    if (!open) return;

    const nextDisplayName = layer?.displayName ?? '';
    const nextDescription = layer?.description ?? '';

    setDisplayName(nextDisplayName);
    setDescription(nextDescription);
    setCharCount(nextDescription.length);
    setValidationErrors({});
  }, [open, layer]);

  const handleSubmit = () => {
    const errors: typeof validationErrors = {};

    if (!displayName || !displayName.trim()) {
      errors.displayName = 'Schermnaam is verplicht';
    }

    if (description && description.length > MAX_DESCRIPTION_LENGTH) {
      errors.description = 'Omschrijving mag maximaal 2000 tekens bevatten';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    onSubmit({
      ...layer,
      displayName: displayName.trim(),
      description: description.trim() || undefined,
    } as LayerViewModel);

    onClose();
  };

  return (
    <DialogBase
      open={open}
      title="Organisatie kaartlaag wijzigen"
      contentText=""
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Opslaan"
      confirmButtonIcon={<Icon>save</Icon>}
      width="900px"
      minWidth="700px"
    >
      <FormControl fullWidth required sx={{ my: 1 }}>
        <TextField
          required
          label="Schermnaam"
          variant="filled"
          value={displayName}
          placeholder="Bijv. Bedrijfsmiddelen kaartlaag"
          onChange={(e) => setDisplayName(e.target.value)}
          error={!!validationErrors.displayName}
          helperText={validationErrors.displayName}
        />
      </FormControl>

      <FormControl fullWidth sx={{ my: 1 }}>
        <TextField
          label="Omschrijving"
          variant="filled"
          multiline
          rows={5}
          value={description}
          placeholder="Optioneel: korte toelichting voor beheer"
          onChange={(e) => {
            setDescription(e.target.value);
            setCharCount(e.target.value.length);
          }}
          error={!!validationErrors.description}
          helperText={validationErrors.description}
          inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
        />
        <FormHelperText sx={{ textAlign: 'right' }}>
          {charCount} / {MAX_DESCRIPTION_LENGTH}
        </FormHelperText>
      </FormControl>
    </DialogBase>
  );
};

export default DialogAssetLayer;